import React, { useState } from "react";
import PropTypes from "prop-types";
import { 
  Heart, 
  MessageCircle, 
  Share2, 
  Bookmark, 
  MapPin, 
  Clock,
  X,
  MoreHorizontal,
  Send
} from "lucide-react";
import AudioWaveform from "../Carousel/Carousel";
import "./lightBox.css";

const LightboxGallery = ({ mediaFiles = [], postData, onAddComment }) => {
  const [focusedIndex, setFocusedIndex] = useState(null);
  const [detailMedia, setDetailMedia] = useState(null);
  const [commentText, setCommentText] = useState("");

  const handleMediaClick = (media, event) => {
    event.stopPropagation(); // ✅ PREVENT PARENT POPUP
    setDetailMedia(media);
  };

  const handleDetailClose = (event) => {
    event.stopPropagation();
    setDetailMedia(null);
  };

  const handleContainerClick = (event) => {
    event.stopPropagation();
  };

  const handleCommentSubmit = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (commentText.trim() && onAddComment) {
      onAddComment(postData.postId, commentText);
      setCommentText("");
    }
  };

  // Format time ago
  const getTimeAgo = (timestamp) => {
    if (!timestamp) return "Just now";
    const now = new Date();
    const posted = new Date(timestamp);
    const diff = Math.floor((now - posted) / 1000);

    if (diff < 60) return `${diff}s ago`;
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  };

  // Render thumbnail media
  const renderThumbnail = (media, index) => {
    const isFocused = focusedIndex === index;

    switch (media.mediatype) {
      case "image":
        return (
          <img
            src={media.mediaurl}
            alt={media.alt || `Media ${index + 1}`}
            className={`lb-media-item ${isFocused ? "lb-focused" : ""}`}
            onClick={(e) => handleMediaClick(media, e)}
            onMouseEnter={() => setFocusedIndex(index)}
            onMouseLeave={() => setFocusedIndex(null)}
          />
        );
      case "video":
        return (
          <div
            className={`lb-media-item lb-video-wrapper ${isFocused ? "lb-focused" : ""}`}
            onClick={(e) => handleMediaClick(media, e)}
            onMouseEnter={() => setFocusedIndex(index)}
            onMouseLeave={() => setFocusedIndex(null)}
          >
            <video src={media.mediaurl} muted className="lb-video-thumbnail" />
            <div className="lb-video-play-icon">▶</div>
          </div>
        );
      case "audio":
        return (
          <div
            className={`lb-media-item lb-audio-wrapper ${isFocused ? "lb-focused" : ""}`}
            onClick={(e) => handleMediaClick(media, e)}
            onMouseEnter={() => setFocusedIndex(index)}
            onMouseLeave={() => setFocusedIndex(null)}
          >
            <div className="lb-audio-icon">🎵</div>
            <span className="lb-audio-label">Audio</span>
          </div>
        );
      default:
        return null;
    }
  };

  // Render detail popup media
  const renderDetailMedia = (media) => {
    if (!media) return null;

    switch (media.mediatype) {
      case "image":
        return (
          <img
            src={media.mediaurl}
            alt={media.alt || "Media"}
            className="lb-detail-media-content"
          />
        );
      case "video":
        return (
          <video
            src={media.mediaurl}
            className="lb-detail-media-content"
            controls
            autoPlay
          />
        );
      case "audio":
        return (
          <div className="lb-detail-audio-wrapper">
            <AudioWaveform src={media.mediaurl} />
            <audio src={media.mediaurl} controls autoPlay />
          </div>
        );
      default:
        return null;
    }
  };

  // Handle single media
  if (mediaFiles.length === 1) {
    return (
      <>
        <div className="lb-single-media" onClick={handleContainerClick}>
          {renderThumbnail(mediaFiles[0], 0)}
        </div>

        {/* Detail Popup for Single Media */}
        {detailMedia && (
          <DetailPopup
            media={detailMedia}
            postData={postData}
            onClose={handleDetailClose}
            renderMedia={renderDetailMedia}
            commentText={commentText}
            setCommentText={setCommentText}
            handleCommentSubmit={handleCommentSubmit}
            getTimeAgo={getTimeAgo}
          />
        )}
      </>
    );
  }

  // Determine grid layout based on media count
  const getGridClass = () => {
    const count = mediaFiles.length;
    if (count === 2) return "lb-grid-2";
    if (count === 3) return "lb-grid-3";
    if (count === 4) return "lb-grid-4";
    if (count <= 6) return "lb-grid-6";
    return "lb-grid-many";
  };

  return (
    <>
      <div
        className={`lb-container ${getGridClass()}`}
        onClick={handleContainerClick}
      >
        {mediaFiles.map((media, index) => (
          <div key={index} className="lb-grid-item">
            {renderThumbnail(media, index)}
          </div>
        ))}
      </div>

      {/* Detail Popup */}
      {detailMedia && (
        <DetailPopup
          media={detailMedia}
          postData={postData}
          onClose={handleDetailClose}
          renderMedia={renderDetailMedia}
          commentText={commentText}
          setCommentText={setCommentText}
          handleCommentSubmit={handleCommentSubmit}
          getTimeAgo={getTimeAgo}
        />
      )}
    </>
  );
};

// Detail Popup Component
const DetailPopup = ({
  media,
  postData,
  onClose,
  renderMedia,
  commentText,
  setCommentText,
  handleCommentSubmit,
  getTimeAgo,
}) => {
  return (
    <div className="lb-detail-overlay" onClick={onClose}>
      <div className="lb-detail-modal" onClick={(e) => e.stopPropagation()}>
        {/* Close Button */}
        <button className="lb-detail-close" onClick={onClose} aria-label="Close">
          <X size={24} />
        </button>

        {/* Left Side - Media */}
        <div className="lb-detail-media">
          {renderMedia(media)}
        </div>

        {/* Right Side - Post Details */}
        <div className="lb-detail-sidebar">
          {/* Header */}
          <div className="lb-detail-header">
            <div className="lb-detail-user">
              <img
                src={postData?.profileImage || "/default-avatar.png"}
                alt={postData?.username || "User"}
                className="lb-detail-avatar"
              />
              <div className="lb-detail-user-info">
                <div className="lb-detail-username">
                  {postData?.username || "Anonymous"}
                </div>
                {postData?.location && (
                  <div className="lb-detail-location">
                    <MapPin size={12} />
                    <span>{postData.location}</span>
                  </div>
                )}
              </div>
            </div>
            <button className="lb-detail-menu">
              <MoreHorizontal size={20} />
            </button>
          </div>

          {/* Description */}
          {postData?.descriptions && (
            <div className="lb-detail-description">
              <span className="lb-detail-desc-username">{postData.username}</span>
              <span className="lb-detail-desc-text">{postData.descriptions}</span>
            </div>
          )}

          {/* Comments Section */}
          <div className="lb-detail-comments">
            {postData?.comments && postData.comments.length > 0 ? (
              postData.comments.map((comment, index) => (
                <div key={index} className="lb-detail-comment">
                  <img
                    src={comment.userImage || "/default-avatar.png"}
                    alt={comment.username}
                    className="lb-detail-comment-avatar"
                  />
                  <div className="lb-detail-comment-content">
                    <div className="lb-detail-comment-text">
                      <span className="lb-detail-comment-username">
                        {comment.username}
                      </span>
                      <span>{comment.text}</span>
                    </div>
                    <div className="lb-detail-comment-meta">
                      <span>{getTimeAgo(comment.timestamp)}</span>
                      {comment.likes > 0 && <span>{comment.likes} likes</span>}
                      <button>Reply</button>
                    </div>
                  </div>
                  <button className="lb-detail-comment-like">
                    <Heart size={14} />
                  </button>
                </div>
              ))
            ) : (
              <div className="lb-detail-no-comments">
                <MessageCircle size={48} />
                <p>No comments yet</p>
                <span>Start the conversation</span>
              </div>
            )}
          </div>

          {/* Footer - Actions */}
          <div className="lb-detail-footer">
            {/* Action Buttons */}
            <div className="lb-detail-actions">
              <div className="lb-detail-actions-left">
                <button className="lb-detail-action-btn" aria-label="Like">
                  <Heart size={24} />
                </button>
                <button className="lb-detail-action-btn" aria-label="Comment">
                  <MessageCircle size={24} />
                </button>
                <button className="lb-detail-action-btn" aria-label="Share">
                  <Share2 size={24} />
                </button>
              </div>
              <button className="lb-detail-action-btn" aria-label="Save">
                <Bookmark size={24} />
              </button>
            </div>

            {/* Stats */}
            <div className="lb-detail-stats">
              <div className="lb-detail-likes">
                {postData?.likes || 0} likes
              </div>
              <div className="lb-detail-time">
                <Clock size={12} />
                <span>{getTimeAgo(postData?.timestamp)}</span>
              </div>
            </div>

            {/* Suggests */}
            {postData?.suggests && postData.suggests > 0 && (
              <div className="lb-detail-suggests">
                💡 {postData.suggests} people suggested this
              </div>
            )}

            {/* Comment Input */}
            <form className="lb-detail-comment-form" onSubmit={handleCommentSubmit}>
              <input
                type="text"
                placeholder="Add a comment..."
                value={commentText}
                onChange={(e) => setCommentText(e.target.value)}
                className="lb-detail-comment-input"
              />
              <button
                type="submit"
                disabled={!commentText.trim()}
                className="lb-detail-comment-submit"
              >
                <Send size={20} />
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

LightboxGallery.propTypes = {
  mediaFiles: PropTypes.arrayOf(
    PropTypes.shape({
      mediatype: PropTypes.oneOf(["image", "audio", "video"]).isRequired,
      mediaurl: PropTypes.string.isRequired,
      alt: PropTypes.string,
    })
  ).isRequired,
  postData: PropTypes.shape({
    postId: PropTypes.string,
    username: PropTypes.string,
    profileImage: PropTypes.string,
    location: PropTypes.string,
    descriptions: PropTypes.string,
    timestamp: PropTypes.string,
    likes: PropTypes.number,
    comments: PropTypes.array,
    suggests: PropTypes.number,
  }),
  onAddComment: PropTypes.func,
};

export default LightboxGallery;
